import { useCallback } from "react";
import { useParams } from "react-router-dom";
import { useLinkCart } from "@/api/hooks";
import { ApiError, expressCartApi, isCapRequired } from "@/api/client";
import type { ExpressCartData } from "@/api/express-cart-loaders";
import { ErrorMessage } from "@/components/ErrorMessage";
import { LoadingSpinner } from "@/components/LoadingSpinner";
// import { trackCheckoutError } from "@/lib/analytics";
import { ExpressCartView } from "@/components/express-cart/ExpressCartView";
import { CaptchaGate } from "@/components/express-cart/CaptchaGate";
import type { CartSession } from "@/hooks/useCartSession";
import { saveExpressSession } from "@/lib/express-cart/device";

const MESSAGES = {
  "not-found": { title: "Link not found", body: "This checkout link doesn't exist or is no longer active." },
  expired: { title: "Link expired", body: "This checkout link has expired." },
  unavailable: { title: "Link currently unavailable", body: "This checkout link may be inaccessible or expired." },
  error: { title: "Something went wrong", body: "We couldn't load this checkout. Please try again." },
} as const;

function errorKind(error: unknown): keyof typeof MESSAGES {
  if (error instanceof ApiError && error.statusCode === 404) return "not-found";
  if (error instanceof ApiError && error.statusCode === 410) return "expired";
  if (
    error instanceof ApiError &&
    error.statusCode === 403 &&
    error.message === "Retailer subscription is inactive"
  ) return "unavailable";
  return "error";
}

function LinkCart({ data }: { data: ExpressCartData }) {
  // Created on the first saved edit, then reused by this browser for this slug.
  const createSession = useCallback(async (): Promise<CartSession> => {
    const created = await expressCartApi.createSession({ retailerId: data.brand.id, linkSlug: data.link.code });
    saveExpressSession(data.link.code, created);
    return { session_id: created.session_id, session_token: created.session_token };
  }, [data.brand.id, data.link.code]);

  return <ExpressCartView data={data} source="link" createSession={createSession} />;
}

/**
 * Express cart from a brand link: a shopper opens /cart/{custom_slug} and lands
 * on a cart pre-filled with the link's products (merged with the cart of the
 * session this browser already has for the brand, if any). Rendered outside
 * SessionGate: the checkout session is only stored when the shopper continues.
 */
export function ExpressCartPage() {
  const { code: rawCode } = useParams<{ code: string }>();
  const slug = rawCode ? decodeURIComponent(rawCode) : null;
  const { data, isLoading, error, refetch } = useLinkCart(slug);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-(--color-bg-secondary)">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // A refused load (429 requiresCap) retries through the CAP gate instead.
  if (error && isCapRequired(error)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-(--color-bg-secondary) p-4">
        <CaptchaGate error={error} onRetry={refetch} />
      </div>
    );
  }

  if (!slug || error || !data) {
    const message = MESSAGES[slug ? errorKind(error) : "not-found"];
    const detail =
      import.meta.env.DEV && error instanceof Error && errorKind(error) === "error" ? error.message : undefined;
    return (
      <div className="min-h-screen flex items-center justify-center bg-(--color-bg-secondary) p-4">
        <ErrorMessage
          title={message.title}
          message={message.body}
          detail={detail}
          className="w-full max-w-md"
        />
      </div>
    );
  }

  return <LinkCart key={slug} data={data} />;
}
